import { Link, useRouterState } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { splitTargetLink } from '../lib/alert-link'
import { PexIcon } from './icons/PexIcon'
import type { PexIconName } from './icons/pex-icon-registry'
import type { PexIconTone } from './icons/pex-icon-types'
import { useT } from '../i18n'
import { alertI18n } from '../i18n/shared/alert'
import { apiUrl } from '../lib/api'
import type { Role } from '../store/role'
import type { AlertsResponse } from './AlertBanner'
import { cn } from '../lib/utils'

const STYLES: Record<
  'warning' | 'info',
  { container: string; icon: PexIconName; tone: PexIconTone; label: string }
> = {
  warning: {
    container: 'border-[var(--color-warning)] bg-[color-mix(in_srgb,var(--color-warning)_9%,var(--color-surface))]',
    icon: 'warning',
    tone: 'warning',
    label: 'text-[var(--color-text)]',
  },
  info: {
    container: 'border-[var(--color-info)] bg-[color-mix(in_srgb,var(--color-info)_9%,var(--color-surface))]',
    icon: 'info',
    tone: 'info',
    label: 'text-[var(--color-text)]',
  },
}

/**
 * AlertCenter — non-critical (warning + info) alerts widget.
 *
 * Header AlertBanner shows ONLY critical severity ("action required now").
 * Warning + info ("action required soon" / "FYI") surface here so they
 * stay visible without dominating the header.
 */
export function AlertCenter() {
  const t = useT(alertI18n)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const activeRole: Role | '' = pathname.startsWith('/admin')
    ? 'admin'
    : pathname.startsWith('/seller')
      ? 'seller'
      : pathname.startsWith('/supply')
        ? 'supply'
        : ''

  const { data } = useQuery<AlertsResponse>({
    queryKey: ['alerts'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/alerts'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    refetchInterval: 60_000,
    enabled: activeRole !== '',
  })

  if (!data) return null

  const filtered = data.data.filter(
    (a) =>
      (a.severity === 'warning' || a.severity === 'info') &&
      a.audience.includes(activeRole as Role),
  )

  if (filtered.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-text-muted)]">
        {t.empty}
      </div>
    )
  }

  const warnings = filtered.filter((a) => a.severity === 'warning')
  const infos = filtered.filter((a) => a.severity === 'info')

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-[var(--color-text)]">{t.title}</h2>
        <div className="text-xs text-[var(--color-text-muted)] tabular-nums">
          {warnings.length > 0 && (
            <span className="text-[var(--color-warning)]">⚠ {warnings.length}</span>
          )}
          {warnings.length > 0 && infos.length > 0 && (
            <span className="mx-1 text-[var(--color-text-muted)]">·</span>
          )}
          {infos.length > 0 && <span className="text-[var(--color-info)]">ⓘ {infos.length}</span>}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {[...warnings, ...infos].map((alert) => {
          const style = STYLES[alert.severity as 'warning' | 'info']
          const body = (
            <>
              <PexIcon name={style.icon} className="mt-0.5" size="xs" tone={style.tone} />
              <div className="flex-1 min-w-0">
                <div className={cn('font-medium leading-5', style.label)}>
                  {alert.title}
                </div>
                <div className="text-xs text-[var(--color-text-muted)] leading-5">
                  {alert.description}
                </div>
              </div>
              {alert.target_link && (
                <span className="shrink-0 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[var(--color-text)]">
                  {t.openAction}
                  <PexIcon name="forward" className="h-3 w-3" size="xs" tone="inherit" />
                </span>
              )}
            </>
          )
          const containerClass = cn(
            'flex items-start gap-3 rounded-md border px-3 py-2 text-sm',
            style.container,
            alert.target_link && 'cursor-pointer transition-colors hover:brightness-95',
          )
          if (alert.target_link) {
            const target = splitTargetLink(alert.target_link)
            return (
              <Link
                key={alert.id}
                to={target.to}
                search={target.search}
                className={containerClass}
              >
                {body}
              </Link>
            )
          }
          return (
            <div key={alert.id} className={containerClass}>
              {body}
            </div>
          )
        })}
      </div>
    </div>
  )
}
